import React from "react";
import { Ruler } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

// Garment flat-lay measurements (inches). Streetwear oversized cut.
const TOPS = {
  headers: ["Size", "Chest", "Length"],
  rows: [
    ["S", "20\"", "28\""],
    ["M", "22\"", "29\""],
    ["L", "24\"", "30\""],
    ["XL", "26\"", "31\""],
    ["XXL", "28\"", "32\""],
  ],
  note: "Oversized fit — size down for a regular fit. Flat-lay garment measurements.",
};

const BOTTOMS = {
  headers: ["Size", "Waist", "Hip"],
  rows: [
    ["S", "28–30\"", "36\""],
    ["M", "30–32\"", "38\""],
    ["L", "32–34\"", "40\""],
    ["XL", "34–36\"", "42\""],
    ["XXL", "36–38\"", "44\""],
  ],
  note: "Relaxed streetwear cut. Elastic/drawstring waists stretch ~2\".",
};

const KIDS = {
  headers: ["Size", "Age", "Chest"],
  rows: [
    ["S", "3–4y", "13\""],
    ["M", "5–6y", "14\""],
    ["L", "7–8y", "15\""],
    ["XL", "9–11y", "16\""],
    ["XXL", "12–13y", "17\""],
  ],
  note: "Youth sizing. If between sizes, size up for longer wear.",
};

const ONESIZE = {
  Socks: "One size fits most — US Men 6–12 / US Women 7–13. Cushioned crew.",
  Hat: "One size — adjustable snapback / structured fit.",
  Beanie: "One size — stretch ribbed knit fits most.",
  Scarf: "One size — 50\" × 50\" lightweight poly.",
  "Face Mask": "One size — adjustable ear loops, ~7.3\" × 4.5\".",
  Gloves: "One size — stretch fit.",
  "Add-On": "Accessory — see product photo for dimensions.",
};

function chartFor(category) {
  if (["Tee", "Hoodie", "Tank", "Long Sleeve", "Couple", "Workwear", "Set"].includes(category)) return TOPS;
  if (["Shorts", "Pants", "Swim", "Boxers"].includes(category)) return BOTTOMS;
  if (category === "Kids") return KIDS;
  return null;
}

export default function SizeGuide({ category }) {
  const chart = chartFor(category);
  const oneSizeNote = ONESIZE[category];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          data-testid="size-guide-trigger"
          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white/55 hover:text-neon transition"
        >
          <Ruler size={13} /> Size Guide
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid="size-guide-dialog"
        className="bg-[#0b0b0d] border border-white/12 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase tracking-tight text-white">
            {category} Size Guide
          </DialogTitle>
        </DialogHeader>

        {chart ? (
          <div className="mt-2">
            <table className="w-full text-sm" data-testid="size-guide-table">
              <thead>
                <tr className="text-neon text-[11px] uppercase tracking-[0.16em]">
                  {chart.headers.map((h) => (
                    <th key={h} className="text-left font-bold pb-3">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chart.rows.map((r) => (
                  <tr key={r[0]} className="border-t border-white/10">
                    {r.map((c, i) => (
                      <td key={chart.headers[i]} className={`py-2.5 ${i === 0 ? "font-bold text-white" : "text-white/70"}`}>
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-4 text-xs text-white/50 leading-relaxed">{chart.note}</p>
          </div>
        ) : (
          <div className="mt-2 rounded-lg border border-white/10 bg-white/5 p-5">
            <p className="text-sm text-white/75 leading-relaxed">
              {oneSizeNote || "One size. See the product photos for fit and dimensions."}
            </p>
          </div>
        )}

        <p className="mt-2 text-[11px] text-white/40">
          Measurements are approximate. Made-to-order — please allow a small tolerance.
        </p>
      </DialogContent>
    </Dialog>
  );
}
